import PageSection from '../Layout/PageSection/PageSection';
import { ImageCard } from './ImageCard';

const OneImage = ({ images }: { images: ImageCardBlok[] }) => (
  <PageSection>
    <div className="grid-section">
      {images.map((image) => (
        <div
          key={image._uid}
          className="col-span-12 md:col-span-10 md:col-start-2"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 12, ratio: image.ratio },
              md: { span: 8, ratio: image.ratio },
              lg: { span: 8, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
  </PageSection>
);

const TwoImages = ({ images }: { images: ImageCardBlok[] }) => (
  <PageSection>
    <div className="grid-section">
      {images.map((image) => (
        <div
          key={image._uid}
          className="col-span-6 flex flex-col justify-center md:col-span-5 first:md:col-start-2"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 6, ratio: image.ratio },
              md: { span: 4, ratio: image.ratio },
              lg: { span: 4, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
  </PageSection>
);

const ThreeImages = ({
  fullWidth = false,
  images,
}: {
  images: ImageCardBlok[];
  fullWidth?: boolean;
}) => (
  <PageSection fullWidth={fullWidth}>
    <div className="grid-section">
      {images.map((image) => (
        <div
          key={image._uid}
          className="col-span-6 last:hidden md:col-span-4 md:last:block"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 6, ratio: image.ratio },
              md: { span: 4, ratio: image.ratio },
              lg: { span: 4, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
  </PageSection>
);

const FourImages = ({ images }: { images: ImageCardBlok[] }) => (
  <PageSection>
    <div className="grid-section mb-3 md:mb-6">
      {images.slice(0, 1).map((image) => (
        <div className="col-span-10 col-start-2 md:col-span-8 md:col-start-3">
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 12, ratio: image.ratio },
              md: { span: 8, ratio: image.ratio },
              lg: { span: 8, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
    <ThreeImages fullWidth images={images.slice(1, 4)} />
  </PageSection>
);

const InlineImages = ({ images }: { images: ImageCardBlok[] }) => (
  <div className="flex w-full flex-col flex-nowrap items-center justify-center gap-y-3 overflow-hidden">
    <div className="flex w-full shrink-0 flex-nowrap items-center justify-center gap-3 overflow-hidden">
      {images.map((image) => (
        <div
          key={image._uid}
          className="w-full max-w-48 shrink-0 sm:max-w-60 md:max-w-72 lg:max-w-96"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 6, ratio: image.ratio },
              md: { span: 3, ratio: image.ratio },
              lg: { span: 4, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
  </div>
);

const MultipleImages = ({
  images,
  max,
}: {
  images: ImageCardBlok[];

  max: number;
}) => (
  <div className="flex w-full flex-col flex-nowrap items-center justify-center gap-y-3 overflow-hidden">
    <div className="flex w-full shrink-0 flex-nowrap items-center justify-center gap-3 overflow-hidden">
      {images.slice(0, max).map((image) => (
        <div
          key={image._uid}
          className="w-full max-w-48 shrink-0 sm:max-w-60 md:max-w-72 lg:max-w-96"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 6, ratio: image.ratio },
              md: { span: 3, ratio: image.ratio },
              lg: { span: 4, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
    <div className="flex w-full shrink-0 flex-nowrap items-center justify-center gap-3 overflow-hidden">
      {images.slice(max).map((image) => (
        <div
          key={image._uid}
          className="w-full max-w-48 shrink-0 sm:max-w-60 md:max-w-72 lg:max-w-96"
        >
          <ImageCard
            description={image.subtitle}
            image={image.image}
            imageSize={{
              sm: { span: 6, ratio: image.ratio },
              md: { span: 3, ratio: image.ratio },
              lg: { span: 4, ratio: image.ratio },
            }}
            thumbnail={image.thumbnail}
            title={image.title}
          />
        </div>
      ))}
    </div>
  </div>
);

export const ImageGroup = ({ images, layout }: ImageGroupBlok) => {
  if (layout === 'inline') {
    return <InlineImages images={images} />;
  }
  if (images.length === 1) {
    return <OneImage images={images} />;
  }
  if (images.length === 2) {
    return <TwoImages images={images} />;
  }
  if (images.length === 3) {
    return <ThreeImages images={images} />;
  }
  if (images.length < 7) {
    return <FourImages images={images} />;
  }
  if (images.length < 13) {
    return <MultipleImages images={images} max={3} />;
  }
  return <MultipleImages images={images} max={6} />;
};
